// Framework-agnostic design tokens for hand-rolled (non-MUI) components.
//
// The brand/semantic scale (accent, feedback, shadow, radius) is re-exported
// straight from theme.js so there is ONE source of truth — change it there and
// both the MUI theme and these components move together, no drift.
//
// `light` and `fontStack` are the light-surface values hand-rolled overlays
// need (dialogs/popovers render as white cards, like MUI Paper does here).

import { extras } from "theme";
export { extras };

// Lead-status progression — one definition for the desktop grids' progress
// pills (Client / Team Member leads) and the mobile LeadCard accent, which
// each used to hardcode their own copy of an off-brand purple/orange/lime/
// neon map. Sequential blue → amber → sage → green from the semantic tokens;
// the bar length and printed label carry the meaning, color reinforces it.
export const leadStatus = {
  steps: {
    "Attempted First Call": { pct: 25, color: extras.feedback.info },
    "Followed Up": { pct: 50, color: extras.feedback.warning },
    "Meeting Set": { pct: 75, color: extras.accent.main },
    "Closed Deal": { pct: 100, color: extras.feedback.success },
  },
  // Unknown/legacy status strings render as a full success bar (matches the
  // old fallback) instead of crashing the cell renderer.
  fallbackColor: extras.feedback.success,
  // Sage-tinted translucent track — replaces the pale-blue "#ebf5fb"
  // light-mode leftover, and reads correctly on both white and dark surfaces.
  track: "rgba(127, 176, 154, 0.18)",
};

export const fontStack =
  "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

// Light-surface tokens — for components that sit on a white card rather than
// the dark teal app chrome.
export const light = {
  surface: "#ffffff",
  textHigh: "#0d0d0d", // matches palette.secondary
  textMid: "#4a5b57",
  textLow: "#6f8782",
  border: "rgba(13, 47, 44, 0.12)",
  borderStrong: "rgba(13, 47, 44, 0.22)",
  overlay: "rgba(11, 20, 18, 0.55)", // backdrop scrim
};

// Responsive page gutter — the classic desktop "1.5rem 2.5rem" margin burned
// 80px (21%) of a 380px phone's width. Every page root uses this instead of
// a hardcoded m string: <Box m={pageMargin}> (Box's m prop accepts the
// responsive object directly). Values match the FM/settings pages that
// already shipped responsive gutters — one scheme, no drift.
export const pageMargin = { xs: "1.25rem 0.75rem", sm: "1.5rem 2.5rem" };

// Welcome-dashboard grid (client / team member / super client): 12 columns,
// 40px-minimum rows that grow to fit (a two-line label on a phone must not
// spill out of its tile), one gap. Tiles declare their own responsive spans —
// no parent "& > div" override, so a feedback tile can sit two-up on a phone.
export const dashboardGridSx = {
  display: "grid",
  gridTemplateColumns: "repeat(12, 1fr)",
  gridAutoRows: "minmax(40px, auto)",
  gap: { xs: "12px", sm: "20px" },
};

// Standard content card — the raised surface every section sits in (Settings,
// the leads filter block, dashboard tiles). Spread into sx.
export const cardSx = (theme) => ({
  backgroundColor: theme.palette.extras.surface.raised,
  backgroundImage: theme.palette.extras.cardSheen,
  border: `1px solid ${theme.palette.extras.surface.borderSubtle}`,
  borderRadius: `${theme.palette.extras.radius.lg}px`,
  boxShadow: theme.palette.extras.shadow.sm,
});

// Dark-surface inks for MUI inputs (standard + outlined) inside a card —
// label/underline/outline/icon in surface tokens, accent on hover/focus.
// One definition for every leads filter block (Admin / Client / Team Member /
// Super Client) instead of a per-page literal.
export const filterInputSx = (theme) => {
  const { surface, accent } = theme.palette.extras;
  return {
    "& .MuiInputLabel-root": { color: surface.textMid },
    "& .MuiInputLabel-root.Mui-focused": { color: accent.soft },
    "& .MuiInput-underline:before": { borderBottomColor: surface.borderDefault },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
      borderBottomColor: accent.soft,
    },
    "& .MuiInput-underline:after": { borderBottomColor: accent.main },
    "& .MuiInputBase-input": { color: surface.textHigh },
    "& .MuiSvgIcon-root": { color: surface.textMid },
    "& .MuiOutlinedInput-root": {
      color: surface.textHigh,
      "& .MuiOutlinedInput-notchedOutline": { borderColor: surface.borderDefault },
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: accent.soft },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: accent.main,
      },
    },
  };
};
